import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LargeTitle, Surface, Pill, EmptyState } from "@/components/ios";
import { BarChart3, Check, X, Camera, PenLine, FileText, Sparkles, Aperture, MapPin } from "lucide-react";

function Stat({ label, value, tone = "var(--text)" }) {
  return (
    <Surface className="p-4">
      <div className="text-2xl font-bold tracking-tight" style={{ color: tone }}>{value}</div>
      <div className="text-xs text-secondary mt-0.5">{label}</div>
    </Surface>
  );
}

const pct = (r) => (r === null || r === undefined ? "—" : `${Math.round(r * 100)}%`);

function AIAccuracy() {
  const [ai, setAi] = useState(null);
  useEffect(() => { api.get("/insights/ai-accuracy").then(({ data }) => setAi(data)).catch(() => setAi({})); }, []);
  if (!ai) return null;
  const im = ai.item_match || {};
  const ws = ai.wide_shots || {};
  const noData = !ai.total_photos_analysed;
  return (
    <div className="mt-8" data-testid="ai-accuracy">
      <div className="px-1 mb-2 flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--text)" }}>
        <Sparkles size={16} className="accent-text" /> AI accuracy <span className="text-xs font-normal text-secondary">— measured from your team's corrections on real inspections</span>
      </div>
      {noData ? (
        <Surface className="p-5 text-sm text-secondary" data-testid="ai-accuracy-empty">No AI-analysed photos yet. As inspectors capture and correct photos, live accuracy appears here.</Surface>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Surface className="p-4" data-testid="ai-match-rate">
              <div className="text-2xl font-bold tracking-tight accent-text">{pct(im.rate)}</div>
              <div className="text-xs text-secondary mt-0.5">Item matches kept ({im.kept}/{im.total})</div>
            </Surface>
            <Stat label="Matches corrected" value={im.corrected ?? 0} tone="var(--danger)" />
            <Surface className="p-4">
              <div className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>{pct(ws.rate)}</div>
              <div className="text-xs text-secondary mt-0.5 flex items-center gap-1"><Aperture size={11} /> Valid wide shots ({ws.valid}/{ws.total})</div>
            </Surface>
            <Surface className="p-4">
              <div className="text-2xl font-bold tracking-tight" style={{ color: ai.room_flags ? "var(--warning)" : "var(--text)" }}>{ai.room_flags ?? 0}</div>
              <div className="text-xs text-secondary mt-0.5 flex items-center gap-1"><MapPin size={11} /> Wrong-room flags</div>
            </Surface>
          </div>

          {ai.most_corrected?.length > 0 && (
            <Surface className="p-4 mt-3" data-testid="ai-most-corrected">
              <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-2">Most-corrected items (where AI most often needs fixing)</div>
              <div className="space-y-1.5">
                {ai.most_corrected.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span style={{ color: "var(--text)" }}>{r.label}</span>
                    <Pill tone="danger">{r.count} corrected</Pill>
                  </div>
                ))}
              </div>
            </Surface>
          )}

          {ai.scene_distribution?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="ai-scene-dist">
              {ai.scene_distribution.map((s) => (
                <Pill key={s.scene} tone="neutral">{s.scene.replace(/_/g, " ")} · {s.count}</Pill>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Insights() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/insights/responses").then(({ data }) => setData(data)); }, []);

  if (!data) return <Surface className="p-6 text-secondary mt-4">Loading…</Surface>;
  const t = data.totals;

  return (
    <div className="pb-10">
      <LargeTitle subtitle="How tenants are responding to condition reports">Response Insights</LargeTitle>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
        <Stat label="Reports out" value={t.reports} />
        <Stat label="Responded" value={t.responded} />
        <Stat label="Items agreed" value={t.agreed} tone="var(--success)" />
        <Stat label="Items disputed" value={t.disputed} tone="var(--danger)" />
        <Stat label="Tenant photos" value={t.tenant_photos} />
        <Stat label="Tenant signed" value={t.signed_tenant} />
      </div>

      <AIAccuracy />

      {data.reports.length === 0 ? (
        <div className="mt-6"><EmptyState icon={BarChart3} title="No issued reports yet" subtitle="Issue a report to a tenant to start seeing responses here." /></div>
      ) : (
        <div className="space-y-2 mt-6">
          <div className="px-1 text-xs font-semibold uppercase tracking-wide text-secondary">Recent reports</div>
          {data.reports.map((r) => (
            <Surface key={r.id} className="p-4 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => nav(`/reports/${r.id}/review`)} testid={`insight-${r.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium truncate" style={{ color: "var(--text)" }}>{r.address || "Property"}</div>
                  <div className="text-xs text-secondary capitalize">{r.report_type} · {r.status?.replace(/_/g, " ")} · {new Date(r.created_at).toLocaleDateString("en-AU")}</div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  {r.responded
                    ? <><span className="flex items-center gap-1" style={{ color: "var(--success)" }}><Check size={14} /> {r.agreed}</span>
                        <span className="flex items-center gap-1" style={{ color: "var(--danger)" }}><X size={14} /> {r.disputed}</span></>
                    : <Pill tone="warning">No response</Pill>}
                  <span className="flex items-center gap-1 text-secondary"><Camera size={14} /> {r.tenant_photos}</span>
                  {r.signed_parties?.length > 0 && (
                    <span className="flex items-center gap-1 text-secondary" title={r.signed_parties.join(", ")}><PenLine size={14} /> {r.signed_parties.length}</span>
                  )}
                </div>
              </div>
            </Surface>
          ))}
        </div>
      )}
    </div>
  );
}
